import { BrowserUtils } from '@azure/msal-browser'
import type { NavigationGuardReturn, RouteLocationNormalized, RouteMap, Router } from 'vue-router'

import { useAuth } from '@/auth/useAuth'
import { useRole } from '@/composables/useRoles'
import { Role } from '@/opendoors-api/client'

const homePage: keyof RouteMap = 'home'
const loginPage: keyof RouteMap = 'LoginPage'
const authRedirectPage: keyof RouteMap = 'AuthRedirect'
const dashboardPage: keyof RouteMap = 'dashboard'
const authErrorPage: keyof RouteMap = 'unauthorized'
const allRoles = Object.values(Role) as Role[]
const startPages: Record<Role, keyof RouteMap> = {
  [Role.ADMIN]: 'customer-list',
  [Role.DEURGETEST]: '/dashboard/deurgetest/',
  [Role.CUSTOMER]: 'customer-order-list',
  [Role.APPLICATION]: 'customer-order-list',
  [Role.CP_USER]: 'unauthorized'
}

/**
 * sessionStorage key used to persist the intended deep-link destination
 * across the full-page Microsoft Entra ID (MSAL) OAuth redirect cycle.
 *
 * Flow:
 *   1. Unauthenticated user opens a deep link (e.g. /dashboard/deurgetest/ABC-123).
 *   2. authGuard fires, writes fullPath to sessionStorage, redirects to /login.
 *   3. User clicks "Sign in with Microsoft" → full-page redirect to Microsoft.
 *   4. Microsoft redirects back to /login/redirect (AuthRedirect).
 *   5. authGuard fires for AuthRedirect, handleRedirectPromise() resolves with account.
 *   6. authorization() reads + clears sessionStorage and navigates to the original path.
 *
 * sessionStorage is used (not localStorage) because:
 *   - It persists through the full-page MSAL redirect within the same tab.
 *   - It is tab-isolated, so two tabs with different deep links don't interfere.
 *   - It is automatically cleared when the tab closes.
 */
export const AUTH_REDIRECT_KEY = 'opendoors:auth:redirect'

export async function authGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const { handleRedirect, getAuthenticated } = useAuth()
  const { hasRole } = useRole()

  const isAuthRedirect = to.name === authRedirectPage
  const isLogin = to.name === loginPage

  if (isAuthRedirect && (BrowserUtils.isInPopup() || BrowserUtils.isInIframe())) {
    // Handle MSAL redirect responses for iframe/popup without running full app flow.
    const { broadcastResponseToMainFrame } = await import('@azure/msal-browser/redirect-bridge')
    await broadcastResponseToMainFrame().catch((err) => {
      console.error(err)
    })
    return true
  }

  const shouldHandleRedirect = isAuthRedirect || isLogin
  const isAuth = shouldHandleRedirect
    ? (await handleRedirect()) || (await getAuthenticated())
    : await getAuthenticated()

  if (!shouldHandleRedirect && to.meta.doesNotRequireAuth) {
    return true
  }

  if (isAuth) {
    return authorization(to, hasRole)
  }

  if (isLogin) {
    return true
  }

  // Persist the full destination path (including params and query) so it can be
  // restored after the MSAL round-trip. The ?from= query param is also kept on
  // the login URL for potential future use by other code.
  sessionStorage.setItem(AUTH_REDIRECT_KEY, to.fullPath)
  return { name: loginPage, query: { from: to.name } } as NavigationGuardReturn
}

export function registerGuards(router: Router) {
  // Add global beforeEach guard
  router.beforeEach(authGuard)
}

async function authorization(
  to: RouteLocationNormalized,
  hasRole: (...rs: Role[]) => Promise<Role | false>
): Promise<NavigationGuardReturn> {
  const requiredRoles =
    (to.meta.requiredRoles as Role[]) ||
    (to.meta.doesNotRequireAuth ? allRoles : [Role.ADMIN, Role.DEURGETEST])
  const role = await hasRole(...requiredRoles)
  if (role) {
    // Restore deep-link destination persisted before the MSAL redirect cycle.
    // This handles the case where an unauthenticated user opened a direct URL
    // (e.g. a shared /dashboard/deurgetest/ABC-123 link), was sent to /login,
    // completed Microsoft authentication, and landed on /login/redirect.
    const intendedPath = sessionStorage.getItem(AUTH_REDIRECT_KEY)
    if (intendedPath) {
      sessionStorage.removeItem(AUTH_REDIRECT_KEY)
      return { path: intendedPath } as NavigationGuardReturn
    }

    // For now, we don't really have a 'landingspage',
    // so we redirect by role:
    if ([homePage, loginPage, dashboardPage].includes(to.name)) {
      return { name: startPages[role] } as NavigationGuardReturn
    }
    return true
  }
  return { name: authErrorPage, query: { from: to.name } } as NavigationGuardReturn
}
